
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { usePropertyMutations } from '@/hooks/usePropertyMutations';
import { usePropertyHighlightMutations } from '@/hooks/usePropertyHighlightMutations';
import { useNavigate } from 'react-router-dom';
import PropertyBasicInfo from './PropertyBasicInfo';
import PropertyDetailsForm from './PropertyDetailsForm';
import PropertyFeaturesSelector from './PropertyFeaturesSelector';
import PropertyImagesForm from './PropertyImagesForm';
import PropertyHostSelector from './PropertyHostSelector';
import PropertyNeighborhoodSelector from './PropertyNeighborhoodSelector';
import PropertyHighlightsForm from './PropertyHighlightsForm';
import { Host } from '@/hooks/useHosts';
import { Neighborhood } from '@/hooks/useNeighborhoods';

interface PropertyHighlight {
  title: string;
  description: string;
}

const CreatePropertyForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { createProperty } = usePropertyMutations();
  const { createPropertyHighlights } = usePropertyHighlightMutations();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    shortName: '',
    shortDescription: '',
    longDescription: '',
    airbnbUrl: '',
    price: 0,
    cleaningFee: 0,
    rating: 0,
    propertyFloor: '',
    guests: 1,
    bedrooms: 0,
    bathrooms: 0,
    area: 0,
    latitude: 0,
    longitude: 0,
    available: true,
    featured: false,
  });

  const [images, setImages] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedHost, setSelectedHost] = useState<Host | null>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<Neighborhood | null>(null);
  const [highlights, setHighlights] = useState<PropertyHighlight[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeatureToggle = (featureId: string, checked: boolean) => {
    setSelectedFeatures(prev => 
      checked 
        ? [...prev, featureId]
        : prev.filter(id => id !== featureId)
    );
  };

  const validateHighlights = () => {
    if (highlights.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos una característica destacada",
        variant: "destructive",
      });
      return false;
    }

    for (let i = 0; i < highlights.length; i++) {
      const highlight = highlights[i];
      if (!highlight.title.trim()) {
        toast({
          title: "Error",
          description: `El título de la característica ${i + 1} es requerido`,
          variant: "destructive",
        });
        return false;
      }
      if (!highlight.description.trim()) {
        toast({
          title: "Error",
          description: `La descripción de la característica ${i + 1} es requerida`,
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validations
    if (!formData.title.trim()) {
      toast({
        title: "Error",
        description: "El título es requerido",
        variant: "destructive",
      });
      return;
    }

    if (!formData.shortName.trim()) {
      toast({
        title: "Error",
        description: "El nombre corto es requerido",
        variant: "destructive",
      });
      return;
    }

    if (!formData.shortDescription.trim()) {
      toast({
        title: "Error",
        description: "La descripción corta es requerida",
        variant: "destructive",
      });
      return;
    }

    if (formData.price <= 0) {
      toast({
        title: "Error",
        description: "El precio debe ser mayor a 0",
        variant: "destructive",
      });
      return;
    }

    if (images.length === 0) {
      toast({
        title: "Error",
        description: "Debe agregar al menos una imagen",
        variant: "destructive",
      });
      return;
    }

    if (!validateHighlights()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Crear la propiedad primero
      const locationString = selectedNeighborhood 
        ? `${selectedNeighborhood.city}, ${selectedNeighborhood.state}` 
        : formData.title;

      const property = await createProperty.mutateAsync({
        ...formData,
        location: locationString,
        images,
        features: selectedFeatures,
        hostId: selectedHost?.id,
        neighborhoodId: selectedNeighborhood?.id,
      });

      // Crear los highlights de la propiedad
      if (highlights.length > 0) {
        await createPropertyHighlights.mutateAsync({
          propertyId: property.id,
          highlights,
        });
      }
      
      toast({
        title: "Propiedad creada",
        description: "La propiedad ha sido creada exitosamente",
      });

      navigate('/admin');
    } catch (error) {
      console.error('Error creating property:', error);
      toast({
        title: "Error",
        description: "Error al crear la propiedad. Por favor intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-8">
      <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Información Básica</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyBasicInfo
            formData={formData}
            onInputChange={handleInputChange}
            canEdit={true}
          />
        </CardContent>
      </Card>

      {/* Property Details */}
      <Card>
        <CardHeader>
          <CardTitle>Detalles de la Propiedad</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyDetailsForm
            formData={formData}
            onInputChange={handleInputChange}
            canEdit={true}
          />
        </CardContent>
      </Card>

      {/* Property Highlights */}
      <Card>
        <CardHeader>
          <CardTitle>Características Destacadas</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyHighlightsForm
            highlights={highlights}
            onHighlightsChange={setHighlights}
          />
        </CardContent>
      </Card>

      {/* Location */}
      <Card>
        <CardHeader>
          <CardTitle>Ubicación y Anfitrión</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PropertyHostSelector
            selectedHost={selectedHost}
            onHostSelect={setSelectedHost}
            canEdit={true}
          />
          <PropertyNeighborhoodSelector
            selectedNeighborhood={selectedNeighborhood}
            onNeighborhoodSelect={setSelectedNeighborhood}
            canEdit={true}
          />
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <CardTitle>Amenidades</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyFeaturesSelector
            selectedFeatures={selectedFeatures}
            onFeatureToggle={handleFeatureToggle}
          />
        </CardContent>
      </Card>

      {/* Images */}
      <Card>
        <CardHeader>
          <CardTitle>Imágenes</CardTitle>
        </CardHeader>
        <CardContent>
          <PropertyImagesForm
            images={images}
            onImagesChange={setImages}
          />
        </CardContent>
      </Card>

      {/* Options */}
      <Card>
        <CardHeader>
          <CardTitle>Opciones</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="available"
                checked={formData.available}
                onCheckedChange={(checked) => handleInputChange('available', checked)}
              />
              <label htmlFor="available" className="text-sm font-medium">
                Propiedad disponible
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => handleInputChange('featured', checked)}
              />
              <label htmlFor="featured" className="text-sm font-medium">
                Propiedad destacada
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submit Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/admin')}
          disabled={isSubmitting}
        >
          Cancelar
          </Button>

        <Button
          type="submit"
            disabled={isSubmitting}
            className='bg-brand-primary hover:bg-brand-primary-hover'
        >
          {isSubmitting ? 'Creando...' : 'Crear Propiedad'}
        </Button>
      </div>
      </form>
    </div>
  );
};

export default CreatePropertyForm;
